export type RepairPendingRequest =
  | { type: "CREATE" }
  | {
      type: "ROUND";
      previousRoundId: string;
      action: "RETRY" | "REJECT_AND_RETRY";
      lockedRepairKeys: string[];
    }
  | { type: "PARTIAL" };

export interface RepairSessionTicket {
  sessionId: string;
  sourceStrictJobId: string;
  accessToken: string;
  conditionRevision: number;
  roundId: string;
  jobId: string;
  pendingRequest: RepairPendingRequest | null;
  applicationId: string | null;
}

export class RepairSessionTicketStorageError extends Error {
  constructor() {
    super("无法保存调整方案进度，请检查浏览器是否允许保存本站数据");
    this.name = "RepairSessionTicketStorageError";
  }
}

export function createRepairSessionTicket(
  sourceStrictJobId: string,
  accessToken: string,
  conditionRevision: number
): RepairSessionTicket {
  return {
    sessionId: crypto.randomUUID(),
    sourceStrictJobId,
    accessToken,
    conditionRevision,
    roundId: crypto.randomUUID(),
    jobId: crypto.randomUUID(),
    pendingRequest: { type: "CREATE" },
    applicationId: null
  };
}

export function nextRepairRoundTicket(
  current: RepairSessionTicket,
  pendingRequest: Exclude<RepairPendingRequest, { type: "CREATE" }>
): RepairSessionTicket {
  return {
    ...current,
    roundId: crypto.randomUUID(),
    jobId: crypto.randomUUID(),
    pendingRequest,
    applicationId: null
  };
}

export function readRepairSessionTicket(
  projectId: string
): RepairSessionTicket | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(storageKey(projectId));
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<RepairSessionTicket>;
    if (
      !isUuid(value.sessionId) ||
      !isUuid(value.sourceStrictJobId) ||
      typeof value.accessToken !== "string" ||
      value.accessToken.length < 32 ||
      !Number.isInteger(value.conditionRevision) ||
      (value.conditionRevision ?? -1) < 0 ||
      !isUuid(value.roundId) ||
      !isUuid(value.jobId) ||
      (value.applicationId !== null && !isUuid(value.applicationId)) ||
      !validPending(value.pendingRequest)
    ) {
      throw new Error("invalid ticket");
    }
    return value as RepairSessionTicket;
  } catch {
    clearRepairSessionTicket(projectId);
    return null;
  }
}

export function writeRepairSessionTicket(
  projectId: string,
  ticket: RepairSessionTicket
): void {
  try {
    localStorage.setItem(storageKey(projectId), JSON.stringify(ticket));
  } catch {
    throw new RepairSessionTicketStorageError();
  }
}

export function clearRepairSessionTicket(
  projectId: string,
  expectedSessionId?: string
): void {
  try {
    if (expectedSessionId) {
      const current = readWithoutCleanup(projectId);
      if (current?.sessionId !== expectedSessionId) return;
    }
    localStorage.removeItem(storageKey(projectId));
  } catch {
    // The encrypted server session expires independently after seven days.
  }
}

function validPending(value: unknown): boolean {
  if (value === null) return true;
  if (!value || typeof value !== "object") return false;
  const pending = value as Partial<RepairPendingRequest> & {
    previousRoundId?: unknown;
    lockedRepairKeys?: unknown;
  };
  if (pending.type === "CREATE" || pending.type === "PARTIAL") return true;
  return (
    pending.type === "ROUND" &&
    isUuid(pending.previousRoundId) &&
    (pending.action === "RETRY" || pending.action === "REJECT_AND_RETRY") &&
    Array.isArray(pending.lockedRepairKeys) &&
    pending.lockedRepairKeys.every((item) => typeof item === "string")
  );
}

function readWithoutCleanup(projectId: string): RepairSessionTicket | null {
  const raw = localStorage.getItem(storageKey(projectId));
  return raw ? (JSON.parse(raw) as RepairSessionTicket) : null;
}

function isUuid(value: unknown): value is string {
  return typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(value);
}

function storageKey(projectId: string): string {
  return `sped-scheduler:repair-session:${projectId}`;
}
